import React, { useMemo, useRef, useState } from "react";
import { AtSign, Gavel, StickyNote } from "lucide-react";
import { cn } from "@/lib/utils";
import { MentionMenu } from "@/components/team/TeamBits";
import { useTeam, searchTeam, displayName } from "@/hooks/useTeam";

/** Tracks the @-token under the caret and inserts the picked name. Shared by every note composer. */
export function useMentions(text, setText, taRef) {
  const { all } = useTeam();
  const [mention, setMention] = useState(null);
  const [idx, setIdx] = useState(0);
  const candidates = useMemo(() => (mention ? searchTeam(all, mention.query).slice(0, 6) : []), [all, mention]);
  const track = (value, caret) => {
    const m = /(^|\s)@([^\s@]{0,30})$/.exec(value.slice(0, caret));
    if (m) { setMention({ start: caret - m[2].length - 1, query: m[2] }); setIdx(0); } else setMention(null);
  };
  const pick = (e) => {
    if (!mention) return;
    const name = displayName(e);
    const caret = taRef.current?.selectionStart ?? text.length;
    const next = `${text.slice(0, mention.start)}@${name} ${text.slice(caret)}`;
    setText(next);
    setMention(null);
    requestAnimationFrame(() => { const ta = taRef.current; if (ta) { ta.focus(); const p = mention.start + name.length + 2; ta.setSelectionRange(p, p); } });
  };
  const insertAt = () => {
    const v = `${text}${text && !/\s$/.test(text) ? " " : ""}@`;
    setText(v);
    requestAnimationFrame(() => { const ta = taRef.current; ta?.focus(); ta?.setSelectionRange(v.length, v.length); track(v, v.length); });
  };
  const onKeyDown = (e) => {
    if (!mention || !candidates.length) return false;
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => (i + 1) % candidates.length); return true; }
    if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => (i - 1 + candidates.length) % candidates.length); return true; }
    if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(candidates[idx] || candidates[0]); return true; }
    if (e.key === "Escape") { e.preventDefault(); setMention(null); return true; }
    return false;
  };
  return { mention, idx, candidates, track, pick, insertAt, onKeyDown, clear: () => setMention(null) };
}

/** Small internal-dialogue composer for one work item: @-mentions, «Notat» or «Beslutning». Never a KI turn, never an applied change. */
export function NoteComposer({ onSubmit, placeholder = "Intern dialog om denne posten – @ for å nevne…", testId = "note-composer", busy = false }) {
  const [text, setText] = useState("");
  const [kind, setKind] = useState("notat");
  const taRef = useRef(null);
  const m = useMentions(text, setText, taRef);
  const submit = async () => {
    const t = text.trim();
    if (!t || busy) return;
    await onSubmit(t, { kind });
    setText("");
    setKind("notat");
    m.clear();
  };
  const decision = kind === "beslutning";
  return (
    <div className={cn("relative rounded-[14px] border bg-white transition-colors", decision ? "border-[hsl(var(--navy)/0.45)]" : "border-black/[0.1] focus-within:border-[hsl(var(--navy)/0.4)]")} data-testid={testId} data-kind={kind}>
      {m.mention && m.candidates.length ? <MentionMenu query={m.mention.query} index={m.idx} onPick={m.pick} testId={`${testId}-mentions`} /> : null}
      <textarea ref={taRef} rows={2} value={text} onChange={(e) => { setText(e.target.value); m.track(e.target.value, e.target.selectionStart ?? e.target.value.length); }} onClick={(e) => m.track(e.target.value, e.target.selectionStart ?? 0)}
        onKeyDown={(e) => { if (m.onKeyDown(e)) return; if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
        placeholder={decision ? "Hva ble besluttet, av hvem og hvorfor – dokumenteres her, anvendes ikke automatisk…" : placeholder} className="block w-full resize-none bg-transparent px-3 pt-2.5 text-[13px] leading-[1.55] outline-none placeholder:text-muted-foreground/55" aria-label="Internt notat" data-testid={`${testId}-input`} />
      <div className="flex flex-wrap items-center gap-1.5 px-2 pb-2">
        <div className="inline-flex h-7 items-center rounded-full bg-black/[0.045] p-0.5" role="tablist" aria-label="Notattype">
          <button type="button" role="tab" aria-selected={!decision} onClick={() => setKind("notat")} className={cn("inline-flex h-6 items-center gap-1 rounded-full px-2 text-[11.5px] font-medium text-muted-foreground", !decision && "bg-white text-foreground shadow-[0_1px_2px_hsl(212_40%_16%/0.08)]")} data-testid={`${testId}-kind-notat`}><StickyNote className="h-3 w-3" /> Notat</button>
          <button type="button" role="tab" aria-selected={decision} onClick={() => setKind("beslutning")} className={cn("inline-flex h-6 items-center gap-1 rounded-full px-2 text-[11.5px] font-medium text-muted-foreground", decision && "bg-[hsl(var(--navy))] text-white")} data-testid={`${testId}-kind-beslutning`}><Gavel className="h-3 w-3" /> Beslutning</button>
        </div>
        <button type="button" onClick={m.insertAt} className="inline-flex h-7 items-center gap-1 rounded-full px-2 text-[11.5px] text-muted-foreground hover:bg-black/[0.05] hover:text-foreground" title="Nevn en person eller avdeling" data-testid={`${testId}-mention`}><AtSign className="h-3 w-3" /> Nevn</button>
        <span className="ml-auto hidden text-[11px] text-muted-foreground/60 sm:inline">Lagres på saken · går ikke til KI · endrer ingenting</span>
        <button type="button" onClick={submit} disabled={!text.trim() || busy} className="inline-flex h-7 items-center rounded-full bg-[hsl(var(--navy))] px-3 text-[12px] font-semibold text-white hover:bg-[hsl(var(--ink-2))] disabled:bg-black/[0.08] disabled:text-foreground/40" data-testid={`${testId}-save`}>{decision ? "Noter beslutning" : "Lagre notat"}</button>
      </div>
    </div>
  );
}
